import { BannerSlideProps } from "@omni/types/components/props/bannerSlider.props";
import { DelayValues } from "@omni/types/components/props/shared/delay.values";
import { SpeedValues } from "@omni/types/components/props/shared/speed.values";
import { TrackingProps } from "@omni/types/components/tracking/trackingProps.type";
import { RequireChildrenFor } from "@omni/types/components/utils/children.types";
import { isDefined } from "@omni/utils/helper.utils";
import { createContext, use, useCallback, useState } from "react";
import { Swiper as SwiperCore } from "swiper/types";

import { useTimer } from "@/utils/hooks/timer/useTimer.hook";
import {
    TimerDirection,
    TimerStatus
} from "@/utils/hooks/timer/useTimer.types";

const INTERVAL_MS = 50;

type BannerSliderContextValue = {
    trackingProps: TrackingProps;
    delay: DelayValues; // in milliseconds
    speed: SpeedValues; // in milliseconds
    slides: Array<BannerSlideProps>;
    interval: number;
    activeIndex: number;
    status: TimerStatus;
    slideTo: (index: number) => void;
    slideNext: () => void;
    startTimer: () => void;
    stopTimer: () => void;
    setActiveIndex: React.Dispatch<React.SetStateAction<number>>;
    setMainSwiper: React.Dispatch<React.SetStateAction<SwiperCore | undefined>>;
};

type BannerSliderContextProviderProps = RequireChildrenFor<{
    trackingProps: TrackingProps;
    delay: DelayValues; // in milliseconds
    speed: SpeedValues; // in milliseconds
    slides: Array<BannerSlideProps>;
}>;

export const BannerSliderContext = createContext<
    BannerSliderContextValue | undefined
>(undefined);
BannerSliderContext.displayName = "BannerSliderContext";

export const BannerSliderContextProvider = ({
    trackingProps,
    delay,
    speed,
    slides,
    children
}: BannerSliderContextProviderProps) => {
    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const [activeIndex, setActiveIndex] = useState(0);

    const slideTo = useCallback(
        (index: number) => {
            if (mainSwiper) {
                mainSwiper.slideTo(index);
            }
        },
        [mainSwiper]
    );

    const slideNext = useCallback(() => {
        if (mainSwiper) {
            mainSwiper.slideNext();
        }
    }, [mainSwiper]);

    const { start, status, stop } = useTimer({
        autoRestart: true,
        autoStart: true,
        direction: TimerDirection.Up,
        referenceTime: delay,
        interval: INTERVAL_MS,
        onEnd: slideNext
    });

    const startTimer = () => {
        start();
    };

    const stopTimer = () => {
        stop();
    };

    return (
        <BannerSliderContext
            value={{
                trackingProps: trackingProps,
                delay: delay,
                speed: speed,
                interval: INTERVAL_MS,
                slides: slides,
                activeIndex: activeIndex,
                status: status,
                slideTo: slideTo,
                slideNext: slideNext,
                startTimer: startTimer,
                stopTimer: stopTimer,
                setActiveIndex: setActiveIndex,
                setMainSwiper: setMainSwiper
            }}
        >
            {children}
        </BannerSliderContext>
    );
};

export const useBannerSliderContext = () => {
    const context = use<BannerSliderContextValue | undefined>(
        BannerSliderContext
    );

    if (!isDefined(context)) {
        throw new Error(`${BannerSliderContext.displayName} is undefined.`);
    }

    return context;
};
